'use client';

/**
 * ReviewModal — our brand pop-up for one full review (Senja sunset, 2026-09-26).
 *
 * Replaces Senja's click-to-expand review modal. Structure kept (purple band,
 * avatar overlapping it, close button top-right); fonts, colours and sizes are
 * Aria's §1 values (reviews.css). The review is shown in full, exactly as
 * stored — highlights as <mark>, the same renderer as the Wall of Love.
 *
 * Behaviour: Esc, the close button and a click on the dimmed backdrop close
 * it; focus moves to the close button on open, is kept inside the dialog
 * while open, and returns to the element that opened it. The page behind does
 * not scroll while it is open (CheckoutModal's body-overflow pattern).
 *
 * Round 7 (Lars, 2026-09-27: "Remove that purple bar"): no band. The header is
 * one centred group — the avatar on the left, and on its right the name, the
 * stars and the student type (lib/review-feed.ts studentType), stacked.
 *
 * Round 3 (Lars, 2026-09-26): the close button is the platform's brand close
 * (pf-platform components/ui/modal-close-button.tsx, ported 1:1 — see
 * ModalCloseButton below); a video review opens here too, in a large player
 * that starts playing at once (video cards in the strips never play inline).
 */
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WolItem } from '@/lib/wall-of-love-feed';
import type { ReviewItem } from '@/lib/review-feed';
import { Segments, initialsOf } from '@/app/wall-of-love/_sections/WolParts';
import { StarIcon } from '@/app/wall-of-love/_sections/WolIcons';
import { TestimonialPlayer } from '@/components/video/TestimonialPlayer';
import { TESTIMONIALS_CDN_HOSTNAME, testimonialPosterSrc } from './cdn';
import './reviews.css';

const EXIT_MS = 200;

/**
 * The platform's CloseIcon (pf-platform components/icons/style-d-icons.tsx =
 * Brand icon-close.svg): same viewBox, paths and per-path weights, verbatim.
 */
function CloseIcon({ size }: { size: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 22 22"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.60 4.75 C8.59 9.57 13.12 13.86 17.75 18.05" strokeWidth={1.7} />
      <path d="M17.55 4.55 C12.86 8.63 8.65 13.19 4.55 17.85" strokeWidth={1.6} />
    </svg>
  );
}

/**
 * ModalCloseButton — a 1:1 port of pf-platform components/ui/modal-close-button.tsx
 * ("full" variant: 56x56, radius 12, gold X at size 32, gold-tint wash and a 90deg
 * turn on hover, gold focus ring). Same markup; the CSS is the platform's
 * `.post-popup__ctrl-btn.post-popup__ctrl-btn--close` values under a scoped
 * class (reviews.css `.rv-close`), because the website has no platform globals.
 */
const ModalCloseButton = forwardRef<HTMLButtonElement, { onClick: () => void; className?: string }>(
  function ModalCloseButton({ onClick, className }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={`rv-close${className ? ` ${className}` : ''}`}
        aria-label="Close"
        title="Close"
        onClick={onClick}
        data-testid="review-modal-close"
      >
        <CloseIcon size={32} />
      </button>
    );
  }
);

/**
 * A video review in the pop-up: a large player (full controls) that starts at
 * once. It presses the player's own play button when the video's first data is
 * in, so the player's trim window and sound handling apply exactly as on a click.
 */
function ModalVideo({ item }: { item: WolItem }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!video) return;
    let done = false;
    const go = () => {
      if (done) return;
      const btn = boxRef.current?.querySelector<HTMLButtonElement>('.tplayer__kit-play');
      if (!btn) return;
      done = true;
      btn.click();
    };
    if (video.readyState >= 1) go();
    video.addEventListener('loadedmetadata', go);
    video.addEventListener('canplay', go);
    return () => {
      video.removeEventListener('loadedmetadata', go);
      video.removeEventListener('canplay', go);
    };
  }, [video]);
  if (!item.video) return null;
  const guid = item.video.guid;
  const first = item.name.replace(/^@/, '').split(/\s+/)[0] || item.name;
  return (
    <div ref={boxRef} className="rv-video rv-video--modal" data-testid="review-modal-video">
      <TestimonialPlayer
        source={{ kind: 'bunny', videoId: guid, cdnHostname: TESTIMONIALS_CDN_HOSTNAME }}
        title={item.title ?? `Video testimonial from ${item.name}`}
        poster={testimonialPosterSrc(item.video)}
        tracks={item.video.captions}
        clip={{ trimIn: item.video.trimIn, trimOut: item.video.trimOut }}
        maxHeight="min(540px, calc(100dvh - 240px))"
        playLabel={`Play the video from ${first}`}
        bigPlay="kit"
        onVideoElement={setVideo}
      />
    </div>
  );
}

export function ReviewStars({ rating, className = '' }: { rating: number | null; className?: string }) {
  if (!rating || rating < 1) return null;
  const n = Math.min(5, Math.round(rating));
  return (
    <div className={`rv-stars ${className}`} role="img" aria-label={`${n} / 5`}>
      {Array.from({ length: n }, (_, i) => (
        <StarIcon key={i} />
      ))}
    </div>
  );
}

export function ReviewAvatar({ item, className, eager }: { item: Pick<WolItem, 'avatarUrl' | 'name'>; className: string; eager?: boolean }) {
  if (item.avatarUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        className={className}
        src={item.avatarUrl}
        alt=""
        width={104}
        height={104}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
      />
    );
  }
  return (
    <span className={`${className} rv-initials`} aria-hidden="true">
      {initialsOf(item.name)}
    </span>
  );
}

export default function ReviewModal({ item, onClose }: { item: ReviewItem | null; onClose: () => void }) {
  const [shown, setShown] = useState<ReviewItem | null>(item);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Mount → next frame "open" (so the entrance transition runs); item → null
  // runs the exit and unmounts after it.
  useEffect(() => {
    if (item) {
      returnFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      setShown(item);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
      return () => cancelAnimationFrame(r);
    }
    setOpen(false);
    const t = window.setTimeout(() => {
      setShown(null);
      const back = returnFocusRef.current;
      if (back && document.contains(back)) back.focus({ preventScroll: true });
    }, EXIT_MS);
    return () => window.clearTimeout(t);
  }, [item]);

  // Scroll lock while shown.
  useEffect(() => {
    if (!shown) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shown]);

  useEffect(() => {
    if (open) closeRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Tab stays inside the dialog.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const f = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    []
  );

  // Esc closes it wherever focus is.
  useEffect(() => {
    if (!shown) return;
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onDocKey);
    return () => document.removeEventListener('keydown', onDocKey);
  }, [shown, onClose]);

  if (!shown || typeof document === 'undefined') return null;
  const isVideo = shown.kind === 'video' && !!shown.video;

  return createPortal(
    <div className="rv-modal" data-state={open ? 'open' : 'closed'} onKeyDown={onKeyDown} data-testid="review-modal">
      <div className="rv-modal__backdrop" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className={`rv-modal__panel${isVideo ? ' rv-modal__panel--video' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <ModalCloseButton ref={closeRef} onClick={onClose} className="rv-modal__close" />
        <div className="rv-modal__scroll">
          <div className="rv-modal__body">
            <div className="rv-modal__head" data-testid="review-modal-head">
              <ReviewAvatar item={shown} className="rv-modal__avatar" eager />
              <div className="rv-modal__who">
                <h2 id={titleId} className="rv-modal__name">
                  {shown.name}
                </h2>
                <ReviewStars rating={shown.rating} className="rv-modal__stars" />
                {shown.studentType ? <p className="rv-modal__type">{shown.studentType}</p> : null}
              </div>
            </div>
            {shown.title ? <p className="rv-modal__title">{shown.title}</p> : null}
            {isVideo ? (
              <ModalVideo item={shown} />
            ) : (
              <p className="rv-modal__text" data-testid="review-modal-text">
                <Segments segments={shown.segments} fallback={shown.plain} />
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
